import { Alert, AlertTitle, Box, Grid } from '@mui/material';
import { Suspense, useEffect } from 'react';

import ConsumptionCard from '@/features/energyxplorer/components/card/ConsumptionCard';
import ConsumptionWarningIndicator from '@/features/energyxplorer/components/card/ConsumptionWarningIndicator';
import EnergyCoverageCard from '@/features/energyxplorer/components/EnergyCoverageCard';
import EnergyGroupInfoCard from '@/features/energyxplorer/components/EnergyGroupInfoCard';
import InfrastructureNodeDataGrid from '@/features/energyxplorer/components/InfrastructureNodeDataGrid';
import SeuNodeDataGrid from '@/features/energyxplorer/components/SeuNodeDataGrid';
import { useTranslation } from 'react-i18next';
import { useGetEnergyOverviewDataQuery } from '../../../../api/energy-analysis/calculateEnergySavingsApiSlice';
import GatewayLoadingIndicator from '@/components/indicators/GatewayLoadingIndicator';

const formatNumber = (number) => {
  return new Intl.NumberFormat('da-DK').format(number);
};

const EnergyOverviewTab = (props) => {
  const {
    selectedAccount,
    selectedEnergyGroup,
    locationData,
    timeRange,
    handleSEURowClick,
    setTimestampSummary,
    setTimestampSummarySetBy,
  } = props;
  const { t } = useTranslation('energyxplorer-energy-overview-tab');
  const {
    data: energyOverviewDataResult,
    isLoading: isLoadingEnergyOverview,
    isFetching: isFetchingEnergyOverview,
    error: energyOverviewError,
  } = useGetEnergyOverviewDataQuery(
    {
      accountId: selectedAccount?.AccountId,
      energyGroupId: selectedEnergyGroup?.energyGroupId,
      start: timeRange.start,
      end: timeRange.end,
    },
    {
      skip:
        !selectedAccount?.AccountId ||
        !selectedEnergyGroup?.energyGroupId ||
        !timeRange.start ||
        !timeRange.end ||
        selectedEnergyGroup.accountId !== selectedAccount?.AccountId,
    },
  );

  useEffect(() => {
    if (energyOverviewDataResult) {
      setTimestampSummary(energyOverviewDataResult.TimestampsSummary);
      setTimestampSummarySetBy('Forbrugsoverblikket');
    } else {
      setTimestampSummary(null);
      setTimestampSummarySetBy(null);
    }
  }, [energyOverviewDataResult, setTimestampSummary, setTimestampSummarySetBy]);
  return (
    <Box>
      <GatewayLoadingIndicator
        isLoading={isLoadingEnergyOverview || isFetchingEnergyOverview}
      />
      {energyOverviewError && (
        <Alert
          severity="error"
          variant="standard"
          sx={{
            borderLeft: '6px solid',
            borderColor: 'error.main',
            mb: 2,
          }}
        >
          <AlertTitle>
            {energyOverviewError?.data?.detail?.ErrorType}
          </AlertTitle>
          {energyOverviewError?.data?.detail?.ErrorMessage}
        </Alert>
      )}
      {energyOverviewDataResult &&
        !energyOverviewError &&
        selectedEnergyGroup?.energyTreeNodes.length > 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Suspense fallback={<div>Loading...</div>}>
                <ConsumptionCard
                  energyDataSummary={energyOverviewDataResult.EnergyDataSummary}
                  dataPoints={energyOverviewDataResult.EnergyData}
                  warningSlot={
                    <ConsumptionWarningIndicator
                      data={energyOverviewDataResult}
                    />
                  }
                />
              </Suspense>
            </Grid>

            <Grid item xs={12} md={4}>
              <Suspense fallback={<div>Loading...</div>}>
                <EnergyCoverageCard
                  title={t('energy_coverage_card.title')}
                  description={t('energy_coverage_card.description')}
                  number={0.15}
                  timeRange={timeRange}
                  energyOverviewDataResult={energyOverviewDataResult}
                />
              </Suspense>
            </Grid>

            <Grid item xs={12} sm={12} md={4}>
              <Suspense fallback={<div>Loading...</div>}>
                <EnergyGroupInfoCard
                  title={t('energy_group_info_card.title')}
                  description={t('energy_group_info_card.description')}
                  selectedEnergyGroup={selectedEnergyGroup}
                  locationData={locationData}
                  energyDataSummary={energyOverviewDataResult.EnergyDataSummary}
                  timeRange={timeRange}
                />
              </Suspense>
            </Grid>

            <Grid item xs={12} sm={12} md={12}>
              <Suspense fallback={<div>Loading...</div>}>
                <InfrastructureNodeDataGrid
                  energyOverviewDataResult={energyOverviewDataResult}
                  formatNumber={formatNumber}
                />
              </Suspense>
            </Grid>

            <Grid item xs={12} sm={12} md={12}>
              <Suspense fallback={<div>Loading...</div>}>
                <SeuNodeDataGrid
                  energyOverviewDataResult={energyOverviewDataResult}
                  distributionBoardSummary={
                    energyOverviewDataResult.DistributionBoardsSummary
                  }
                  energyDistributionSummary={
                    energyOverviewDataResult.EnergyDistributionSummary
                  }
                  formatNumber={formatNumber}
                  handleSEURowClick={handleSEURowClick}
                />
              </Suspense>
            </Grid>
          </Grid>
        )}
    </Box>
  );
};

export default EnergyOverviewTab;
